import { Redirect, Tabs } from 'expo-router';
import React from 'react';
import { View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { FloatingTabBar } from '@/components/navigation/FloatingTabBar';
import { DailyStreakGate } from '@/components/rewards/DailyStreakGate';
import { useProfile } from '@/features/profile/hooks';
import { useAuth } from '@/lib/auth/AuthProvider';
import { useHasSeenIntro } from '@/lib/intro';

export default function TabsLayout() {
  const { t } = useTranslation();
  const { session, userId } = useAuth();
  const profile = useProfile();
  const seenIntro = useHasSeenIntro();

  // First launch (signed-out, intro not yet seen): show the branded intro /
  // Quick Setup before anything else. `null` = the flag is still loading; the
  // root already presented the splash, so a blank frame here is imperceptible.
  if (seenIntro === null) {
    return null;
  }
  if (!session && !seenIntro) {
    return <Redirect href="/intro" />;
  }

  // Session is set but the user id hasn't settled yet (the profile query keys off
  // it): hold a frame rather than evaluate a half-loaded profile.
  if (session && !userId) {
    return null;
  }
  // Wait for the profile before deciding, so a signed-in user never flashes the
  // tabs and then gets bounced to onboarding - and, more importantly, never slips
  // INTO the tabs while the profile is still null (which would skip onboarding and
  // silently drop their buffered first-run picks).
  if (session && profile.isLoading) {
    return null;
  }
  // First-run: a signed-in user who hasn't confirmed their display name is sent to
  // the guided picker (which also applies any buffered pre-sign-in follows).
  if (session && profile.data && !profile.data.display_name_confirmed) {
    return <Redirect href="/auth/welcome" />;
  }
  // Profile loaded SUCCESSFULLY but there's no row yet (first-run / trigger lag) ->
  // onboard so the picks aren't lost. A transient fetch ERROR (no data, isError) is
  // deliberately NOT treated as "needs onboarding": React Query keeps the last good
  // value, so a returning confirmed user is never bounced by a momentary blip.
  if (session && profile.isSuccess && !profile.data) {
    return <Redirect href="/auth/welcome" />;
  }

  return (
    <View style={{ flex: 1 }}>
      <Tabs tabBar={(props) => <FloatingTabBar {...props} />} screenOptions={{ headerShown: false }}>
        <Tabs.Screen name="index" options={{ title: t('tabs.matches') }} />
        <Tabs.Screen name="fantasy" options={{ title: t('tabs.fantasy') }} />
        <Tabs.Screen name="games" options={{ title: t('tabs.games') }} />
        <Tabs.Screen name="leaderboard" options={{ title: t('tabs.leaderboard') }} />
        {/* Predictions stays a route (reached via the More menu + the per-match
            poll) but the FloatingTabBar hides it, so it isn't a top-level tab. */}
        <Tabs.Screen name="predictions" options={{ title: t('tabs.predictions') }} />
        <Tabs.Screen name="leagues" options={{ title: t('tabs.leagues') }} />
        <Tabs.Screen name="profile" options={{ title: t('tabs.profile') }} />
      </Tabs>
      {/* Once-per-day login-streak popup (signed-in + not yet checked in). */}
      {session ? <DailyStreakGate /> : null}
    </View>
  );
}
